import { Button, Stack, TextField, Typography } from "@mui/material";
import AccessCheckResults from "./AccessCheckResults";
import CheckingProgress from "./CheckingProgress";
import VerifyCodeForm from "./VerifyCodeForm";
import type { AccessCheckState } from "./accessCheckState";
import { accessCheckButtonSx, accessCheckFieldSx } from "./styles";
import type { BucketClassification } from "./types";

interface Props {
  state: AccessCheckState;
  onStartChecks: () => void;
  onEmailDraftChange: (value: string) => void;
  onEmailSubmit: () => void;
  onResendCode: () => void;
  onVerifySubmit: (code: string) => void;
  onVerifyDone: () => void;
  onDownload: () => void;
  onResetToInitial: () => void;
}

const AccessCheckBody = ({
  state,
  onStartChecks,
  onEmailDraftChange,
  onEmailSubmit,
  onResendCode,
  onVerifySubmit,
  onVerifyDone,
  onDownload,
  onResetToInitial,
}: Props) => {
  switch (state.step) {
    case "initial":
    case "running-checks":
      return <InitialBody running={state.step === "running-checks"} onStart={onStartChecks} />;

    case "collecting-email":
      return (
        <CollectingEmailBody
          email={state.emailDraft}
          error={state.emailFieldError}
          onChange={onEmailDraftChange}
          onSubmit={onEmailSubmit}
        />
      );

    case "requesting-code":
      return <RequestingCodeBody />;

    case "entering-code":
      return (
        <EnteringCodeBody
          submitting={state.submitting}
          resending={state.resending}
          codeError={state.codeError}
          onResend={onResendCode}
          onSubmit={onVerifySubmit}
        />
      );

    case "verifying":
      return <VerifyingBody done={state.verifyDone} onDone={onVerifyDone} />;

    case "show-results":
      if (!state.classification) {
        return null;
      }
      return <ResultsBody classification={state.classification} onDownload={onDownload} onReset={onResetToInitial} />;

    case "diagnostics-failed":
      return <DiagnosticsFailedBody onReset={onResetToInitial} />;
  }
};

const InitialBody = ({ running, onStart }: { running: boolean; onStart: () => void }) => (
  <>
    <Typography color="textSecondary" sx={{ textAlign: "center" }}>
      Something went wrong while accessing the portal.
      <br />
      Run a quick check to find the issue and see what to do next.
    </Typography>
    <Button variant="contained" color="primary" loading={running} onClick={onStart} fullWidth sx={accessCheckButtonSx}>
      Run a quick check
    </Button>
  </>
);

const CollectingEmailBody = ({
  email,
  error,
  onChange,
  onSubmit,
}: {
  email: string;
  error: string | undefined;
  onChange: (value: string) => void;
  onSubmit: () => void;
}) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography color="textSecondary" sx={{ textAlign: "center" }}>
      Enter your email to receive a verification code.
    </Typography>
    <TextField
      label="Email"
      type="email"
      value={email}
      onChange={(event) => onChange(event.target.value)}
      error={Boolean(error)}
      helperText={error}
      autoFocus
      fullWidth
      sx={accessCheckFieldSx}
    />
    <Button
      variant="contained"
      color="primary"
      onClick={onSubmit}
      fullWidth
      sx={accessCheckButtonSx}
      disabled={!email.trim()}
    >
      Send verification code
    </Button>
  </Stack>
);

const RequestingCodeBody = () => (
  <Typography color="textSecondary" sx={{ textAlign: "center" }}>
    Sending a verification code to your email...
  </Typography>
);

const EnteringCodeBody = ({
  submitting,
  resending,
  codeError,
  onResend,
  onSubmit,
}: {
  submitting: boolean;
  resending: boolean;
  codeError: string | undefined;
  onResend: () => void;
  onSubmit: (code: string) => void;
}) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography color="textSecondary" sx={{ textAlign: "center" }}>
      We&apos;ve sent a verification code to your email.
      <br />
      Enter it below to continue the access check.
    </Typography>
    <VerifyCodeForm
      errorText={codeError}
      submitting={submitting}
      resending={resending}
      onSubmit={onSubmit}
      onResend={onResend}
    />
  </Stack>
);

const VerifyingBody = ({ done, onDone }: { done: boolean; onDone: () => void }) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography color="textSecondary" sx={{ textAlign: "center" }}>
      We&apos;ve started checking your access. This will only take a few seconds.
    </Typography>
    <CheckingProgress done={done} onDone={onDone} />
  </Stack>
);

const ResultsBody = ({
  classification,
  onDownload,
  onReset,
}: {
  classification: BucketClassification;
  onDownload: () => void;
  onReset: () => void;
}) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <AccessCheckResults classification={classification} onDownload={onDownload} />
    <Button variant="outlined" color="primary" onClick={onReset} fullWidth sx={accessCheckButtonSx}>
      Run check again
    </Button>
  </Stack>
);

const DiagnosticsFailedBody = ({ onReset }: { onReset: () => void }) => (
  <Stack spacing={2} sx={{ width: "100%" }}>
    <Typography color="textSecondary" sx={{ textAlign: "center" }}>
      We couldn&apos;t complete the diagnostics on this device.
      <br />
      Please try again. If the problem persists, please contact support.
    </Typography>
    <Button variant="contained" color="primary" onClick={onReset} fullWidth sx={accessCheckButtonSx}>
      Run check again
    </Button>
  </Stack>
);

export default AccessCheckBody;
